import React from 'react';
import { Check, Mail } from "lucide-react";
import { Button, Card, CardContent, CardActions, CardHeader, Typography } from '@mui/material';
import Navbar from '../components/Navbar';
import AnimatedBackground from '../components/AnimatedBackground';

const PricingPage = () => {
    // Pricing tiers
    const tiers = [
        {
            name: "Free",
            price: "$0",
            period: "forever",
            description: "Design cloud architecture and generate the Terraform code for any cloud provider.",
            buttonText: "Get Started",
            buttonVariant: "outline",
            features: [
                "Unlimited cloud architectures",
                "Unlimited Terraform code generation",
                "Access to all supported cloud providers - AWS, Azure, GCP and OCI",
                "Access to public templates",
                "Support of public and private Terraform modules"
            ]
        },
        {
            name: "Individual",
            price: "$49",
            period: "/month",
            description: "Best for individual developers and freelancers.",
            buttonText: "Start Free Trial",
            buttonVariant: "default",
            baseFeatures: "All from the Free plan, plus:",
            features: [
                "2 Users",
                "5 Projects",
                "Private templates",
                "Limited deployments",
                "GitHub partial integration",
                "Cost estimation",
                "Change history"
            ]
        },
        {
            name: "Teams",
            price: "$99",
            period: "per user / month",
            description: "Best for small teams.",
            buttonText: "Start Free Trial",
            buttonVariant: "default",
            popular: true,
            baseFeatures: "All from the Individual plan, plus:",
            features: [
                "Unlimited members & teams",
                "Native architecture versioning",
                "Git integration",
                "Embedded & visual CI/CD engine",
                "Remote backend",
                "RBAC",
                "Unlimited deployments"
            ]
        },
        {
            name: "Enterprise",
            price: "Custom",
            period: "",
            description: "Best for enterprise workloads and custom integrations",
            buttonText: "Book a Demo",
            buttonVariant: "default",
            baseFeatures: "All from the Teams plan, plus:",
            features: [
                "Private Self-hosted Runner",
                "SSO",
                "Private registry",
                "Import from Azure® and AWS®",
                "Ability to edit generated code",
                "Self-hosted or single tenant hosting",
                "Audit logs",
                "Guaranteed SLA",
                "Terraform migration assistance",
                "Private onboarding",
                "Dedicated support via Slack or Teams"
            ]
        }
    ];

    // Function to handle the button click
    const handleButtonClick = (tier) => {
        if (tier.name === "Enterprise") {
            window.location.href = "mailto:contact@cloudima.org?subject=Enterprise%20Plan%20Inquiry&body=Hello%20Cloudima%20team,%0A%0AI'm%20interested%20in%20learning%20more%20about%20your%20Enterprise%20Plan.%0A%0APlease%20contact%20me%20with%20more%20information.%0A%0AThank%20you.";
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-900/50 to-indigo-900">
            <AnimatedBackground />
            <Navbar />
            {/* Header */}
            <div className="relative isolate overflow-hidden">
                {/* Background */}
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(45%_25%_at_50%_50%,rgba(56,189,248,0.2),rgba(0,0,0,0))]" />

                <div className="max-w-7xl mx-auto px-6 pt-24 pb-8 lg:px-8">
                    <div className="mx-auto max-w-4xl text-center">
                        <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
                            Pricing Plans
                        </h1>
                        <p className="mt-6 text-lg leading-8 text-gray-300">
                            Choose the right plan for your needs. From individuals to large teams, we have you covered.
                        </p>
                    </div>
                </div>
            </div>

            {/* Mobile view - Cards */}
            <div className="mx-auto max-w-7xl px-6 lg:hidden">
                <div className="space-y-8">
                    {tiers.map((tier) => (
                        <Card key={tier.name} variant="outlined">
                            <CardHeader
                                title={<Typography variant="h5" component="div">{tier.name}</Typography>}
                                subheader={tier.popular ? "Most Popular" : ""}
                            />
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">
                                    {tier.price}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {tier.description}
                                </Typography>
                                <ul>
                                    {tier.features.map((feature, index) => (
                                        <li key={index}>
                                            <Check className="h-4 w-4 text-green-500 mr-2 shrink-0" /> {feature}
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                            <CardActions>
                                <Button
                                    variant={tier.buttonVariant}
                                    fullWidth
                                    onClick={() => handleButtonClick(tier)}
                                >
                                    {tier.name === "Enterprise" ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Mail className="h-4 w-4" />
                                            {tier.buttonText}
                                        </span>
                                    ) : (
                                        tier.buttonText
                                    )}
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Desktop view - Cards side by side */}
            <div className="hidden mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:block">
                <div className="grid grid-cols-4 gap-6">
                    {tiers.map((tier) => (
                        <div
                            key={tier.name}
                            className={`relative h-full rounded-2xl p-8 ring-1 ring-slate-700/50 backdrop-blur-sm flex flex-col ${tier.popular ? 'bg-indigo-900/50 shadow-xl shadow-indigo-500/20 z-10 scale-105' : 'bg-slate-800/40'
                                }`}
                        >
                            {tier.popular && (
                                <div className="mb-4 flex justify-center">
                                    <span className="rounded-full px-3 py-1 text-xs font-semibold leading-5 bg-indigo-400/10 text-indigo-400 ring-1 ring-inset ring-indigo-400/30">
                                        Most Popular
                                    </span>
                                </div>
                            )}

                            <h3 className="text-2xl font-bold text-white text-center">{tier.name}</h3>

                            <p className="mt-6 flex items-baseline justify-center gap-x-2">
                                <span className="text-4xl font-bold tracking-tight text-white">
                                    {tier.price}
                                </span>
                                {tier.price !== "Free" && tier.price !== "Custom" && (
                                    <span className="text-sm font-semibold text-gray-300">/month</span>
                                )}
                            </p>

                            <p className="mt-2 text-sm text-gray-300 text-center">{tier.description}</p>

                            <div className="mt-8 flex-grow">
                                {tier.baseFeatures && (
                                    <p className="text-sm font-semibold text-indigo-200 mb-3">
                                        {tier.baseFeatures}
                                    </p>
                                )}

                                <ul className="space-y-3">
                                    {tier.features.map((feature, index) => (
                                        <li key={index} className="flex items-start text-sm text-gray-300">
                                            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-8">
                                <Button
                                    variant={tier.buttonVariant}
                                    className="w-full"
                                    onClick={() => handleButtonClick(tier)}
                                >
                                    {tier.name === "Enterprise" ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Mail className="h-4 w-4" />
                                            {tier.buttonText}
                                        </span>
                                    ) : (
                                        tier.buttonText
                                    )}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA section */}
            <div className="mx-auto max-w-7xl px-6 py-24">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Ready to get started?
                    </h2>
                    <p className="mt-6 text-lg leading-8 text-gray-300">
                        Contact our sales team for a personalized demo and to discuss your specific needs.
                    </p>
                    <div className="mt-10 flex items-center justify-center gap-x-6">
                        <a
                            href="mailto:contact@cloudima.org?subject=Enterprise%20Plan%20Inquiry"
                            className="rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center gap-2"
                        >
                            <Mail className="h-4 w-4" />
                            Contact Sales
                        </a>
                        <a href="/#features" className="text-sm font-semibold leading-6 text-white">
                            Learn more <span aria-hidden="true">→</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
